import React, { useState } from 'react';
import { View } from 'react-native';
import { SegmentedButtons } from 'react-native-paper';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramDuctSizeFromCFM } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.ductSizeFromCfm.v1';

type Shape = 'round' | 'rect';
type Fields = { shape: Shape; cfm: string; velocity: string; knownSide: string; otherSide: string };
const EMPTY: Fields = { shape: 'round', cfm: '', velocity: '', knownSide: '', otherSide: '' };

/**
 * Duct Size / Flow / Velocity — bidirectional.
 *   Round: A (ft²) = Q/V, D (in) = √(576·Q/(π·V)). Core vars {Q, V, D} (2-of-3).
 *   Rect:  A (ft²) = Q/V, W·H = 144·Q/V. Core vars {Q, V, W, H} (3-of-4).
 */
const AirflowDuctSizeFromCFMScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp, isImperial } = useUnits();

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const calculate = () => {
    setError(null);

    if (fields.shape === 'round') {
      // Core: Q (cfm), V (fpm), D (in). A = Q/V sq ft; D = sqrt(576 Q/(pi V))
      const core = [fields.cfm, fields.velocity, fields.knownSide];
      const filled = core.filter((v) => v !== '').length;
      if (filled < 2) { setError('Fill any 2 of the 3 fields (Q, V, D) to solve for the missing one.'); return; }
      if (filled === 3) { setError('All 3 values are filled — clear one to solve for it.'); return; }

      const q = toImp(fields.cfm, 'flow');
      const v = toImp(fields.velocity, 'velocity');
      const d = toImp(fields.knownSide, 'diameter');

      if (fields.knownSide === '') {
        if (!(q > 0) || !(v > 0)) { setError('Flow and velocity must be greater than zero.'); return; }
        const dIn = Math.sqrt((576 * q) / (Math.PI * v));
        setSolvedFor('knownSide');
        set({ knownSide: fromImp(dIn, 'diameter', 2) });
      } else if (fields.cfm === '') {
        if (!(v > 0) || !(d > 0)) { setError('Velocity and diameter must be greater than zero.'); return; }
        const aSqFt = Math.PI * Math.pow(d, 2) / (4 * 144);
        setSolvedFor('cfm');
        set({ cfm: fromImp(aSqFt * v, 'flow', 2) });
      } else if (fields.velocity === '') {
        if (!(q > 0) || !(d > 0)) { setError('Flow and diameter must be greater than zero.'); return; }
        const aSqFt = Math.PI * Math.pow(d, 2) / (4 * 144);
        setSolvedFor('velocity');
        set({ velocity: fromImp(q / aSqFt, 'velocity', 2) });
      }
    } else {
      // Core: Q, V, W, H
      const core = [fields.cfm, fields.velocity, fields.knownSide, fields.otherSide];
      const filled = core.filter((v) => v !== '').length;
      if (filled < 3) { setError('Fill any 3 of the 4 fields (Q, V, W, H) to solve for the missing one.'); return; }
      if (filled === 4) { setError('All 4 values are filled — clear one to solve for it.'); return; }

      const q = toImp(fields.cfm, 'flow');
      const v = toImp(fields.velocity, 'velocity');
      const w = toImp(fields.knownSide, 'diameter');
      const h = toImp(fields.otherSide, 'diameter');

      if (fields.otherSide === '') {
        if (!(q > 0) || !(v > 0) || !(w > 0)) { setError('Q, V, W must be greater than zero.'); return; }
        const hIn = (144 * q / v) / w;
        setSolvedFor('otherSide');
        set({ otherSide: fromImp(hIn, 'diameter', 2) });
      } else if (fields.knownSide === '') {
        if (!(q > 0) || !(v > 0) || !(h > 0)) { setError('Q, V, H must be greater than zero.'); return; }
        const wIn = (144 * q / v) / h;
        setSolvedFor('knownSide');
        set({ knownSide: fromImp(wIn, 'diameter', 2) });
      } else if (fields.cfm === '') {
        if (!(v > 0) || !(w > 0) || !(h > 0)) { setError('V, W, H must be greater than zero.'); return; }
        const aSqFt = (w * h) / 144;
        setSolvedFor('cfm');
        set({ cfm: fromImp(aSqFt * v, 'flow', 2) });
      } else if (fields.velocity === '') {
        if (!(q > 0) || !(w > 0) || !(h > 0)) { setError('Q, W, H must be greater than zero.'); return; }
        const aSqFt = (w * h) / 144;
        setSolvedFor('velocity');
        set({ velocity: fromImp(q / aSqFt, 'velocity', 2) });
      }
    }
  };

  const clearAll = () => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  };

  // Derived area display
  const qImp = fields.cfm ? toImp(fields.cfm, 'flow') : NaN;
  const vImp = fields.velocity ? toImp(fields.velocity, 'velocity') : NaN;
  const areaSqFt = Number.isFinite(qImp) && Number.isFinite(vImp) && vImp > 0 ? qImp / vImp : NaN;

  const result = [
    fields.cfm && { label: 'Flow', value: fields.cfm, unit: unit('flow'), emphasis: solvedFor === 'cfm' },
    fields.velocity && { label: 'Velocity', value: fields.velocity, unit: unit('velocity'), emphasis: solvedFor === 'velocity' },
    Number.isFinite(areaSqFt) && { label: 'Area', value: fromImp(areaSqFt, 'area', 4), unit: unit('area') },
    fields.shape === 'round' && fields.knownSide && { label: 'Diameter', value: fields.knownSide, unit: unit('diameter'), emphasis: solvedFor === 'knownSide' },
    fields.shape === 'rect' && fields.knownSide && { label: 'Width', value: fields.knownSide, unit: unit('diameter'), emphasis: solvedFor === 'knownSide' },
    fields.shape === 'rect' && fields.otherSide && { label: 'Height', value: fields.otherSide, unit: unit('diameter'), emphasis: solvedFor === 'otherSide' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[];

  return (
    <CalcShell
      title={`Duct Size / Flow / Velocity`}
      subtitle="Bidirectional: fill the known fields to solve for the missing one"
      formula={
        <>
          {isImperial ? (
            <>
              <FormulaLine>A (ft²) = Q / V</FormulaLine>
              <FormulaLine>Round: D (in) = √(576 · Q / (π · V))</FormulaLine>
              <FormulaLine>Rect: W · H (in²) = 144 · Q / V</FormulaLine>
            </>
          ) : (
            <>
              <FormulaLine>A (m²) = Q / (1000 · V)</FormulaLine>
              <FormulaLine>Round: D (mm) = √(4000 · Q / (π · V))</FormulaLine>
              <FormulaLine>Rect: W · H (mm²) = 1000 · Q / V</FormulaLine>
            </>
          )}
          <FormulaNote>V in {unit('velocity')}, Q in {unit('flow')}, sides in {unit('diameter')}.</FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      diagram={<DiagramDuctSizeFromCFM />}
      outputSlots={{
        cfm: fields.cfm ? toImp(fields.cfm, 'flow') : undefined,
        velocity_fpm: fields.velocity ? toImp(fields.velocity, 'velocity') : undefined,
        area_sqft: Number.isFinite(areaSqFt) ? areaSqFt : undefined,
        duct_diameter_in:
          fields.shape === 'round' && fields.knownSide ? toImp(fields.knownSide, 'diameter') : undefined,
        duct_width_in:
          fields.shape === 'rect' && fields.knownSide ? toImp(fields.knownSide, 'diameter') : undefined,
        duct_height_in:
          fields.shape === 'rect' && fields.otherSide ? toImp(fields.otherSide, 'diameter') : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.cfm != null && !fields.cfm) {
          patch.cfm = fromImp(values.cfm, 'flow', 2);
        }
        if (values.velocity_fpm != null && !fields.velocity) {
          patch.velocity = fromImp(values.velocity_fpm, 'velocity', 2);
        }
        if (values.duct_diameter_in != null && !fields.knownSide) {
          patch.knownSide = fromImp(values.duct_diameter_in, 'diameter', 2);
          patch.shape = 'round';
        }
        if (values.duct_width_in != null && !fields.knownSide) {
          patch.knownSide = fromImp(values.duct_width_in, 'diameter', 2);
          patch.shape = 'rect';
        }
        if (values.duct_height_in != null && !fields.otherSide) {
          patch.otherSide = fromImp(values.duct_height_in, 'diameter', 2);
          patch.shape = 'rect';
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <SegmentedButtons
        value={fields.shape}
        onValueChange={(v) => set({ shape: v as Shape })}
        buttons={[
          { value: 'round', label: 'Round' },
          { value: 'rect', label: 'Rectangular' },
        ]}
        style={{ marginBottom: 16 }}
      />

      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Airflow (${unit('flow')})`}
          value={fields.cfm}
          onChangeText={(v) => set({ cfm: v })}
          rightText={unit('flow')}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Velocity (${unit('velocity')})`}
          value={fields.velocity}
          onChangeText={(v) => set({ velocity: v })}
          rightText={unit('velocity')}
          onSubmitEditing={calculate}
        />
      </View>
      {fields.shape === 'round' ? (
        <View style={{ marginBottom: 12 }}>
          <NumericField
            label={`Diameter D (${unit('diameter')})`}
            value={fields.knownSide}
            onChangeText={(v) => set({ knownSide: v })}
            rightText={unit('diameter')}
            onSubmitEditing={calculate}
          />
        </View>
      ) : (
        <>
          <View style={{ marginBottom: 12 }}>
            <NumericField
              label={`Width W (${unit('diameter')})`}
              value={fields.knownSide}
              onChangeText={(v) => set({ knownSide: v })}
              rightText={unit('diameter')}
              onSubmitEditing={calculate}
            />
          </View>
          <View style={{ marginBottom: 12 }}>
            <NumericField
              label={`Height H (${unit('diameter')})`}
              value={fields.otherSide}
              onChangeText={(v) => set({ otherSide: v })}
              rightText={unit('diameter')}
              onSubmitEditing={calculate}
            />
          </View>
        </>
      )}
    </CalcShell>
  );
};

export default AirflowDuctSizeFromCFMScreen;
